import React, { useCallback, useEffect, useState, useContext } from "react";
import { View, Image, Text, useWindowDimensions, Platform } from "react-native";
import { AuthProvider, AuthContext } from "./Context/AuthContext";
import { NavigationContainer } from "@react-navigation/native";
import * as SplashScreen from "expo-splash-screen";
import * as Font from "expo-font";
import { createStackNavigator } from "@react-navigation/stack";
import CompanyLoginScreen from "./screens/CompanyLoginScreen";
import CompanyRegistrationScreen from "./screens/CompanyRegistrationScreen";
import EmployeeLoginScreen from "./screens/EmployeeLoginScreen";
import EmployeeRegistrationScreen from "./screens/EmployeeRegistrationScreen";
import QuestionnaireTopicsScreen from "./screens/QuestionnaireTopicsScreen";
import QuestionsScreen from "./screens/QuestionsScreen";
import Drawer from "./components/DrawerNavigator";
import GlobalStyles from "./components/StylenColor";
import { NetworkProvider } from "./Context/NetworkContext";

const Stack = createStackNavigator();

if (Text.defaultProps == null) {
  Text.defaultProps = {};
}
Text.defaultProps.allowFontScaling = false;

SplashScreen.preventAutoHideAsync();

/* ------------------ ✅ SEPARATE NAVIGATION COMPONENT ------------------ */
const MainNavigator = () => {
  const { companyId, loading } = useContext(AuthContext);
  const { width } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === "web" && width > 900;

  if (loading) return null;

  const linking = {
    prefixes: ["https://esg-data-ea8f7.web.app", "http://localhost:19006"],
    config: {
      screens: {
        CompanyLoginScreen: "login",
        CompanyRegistrationScreen: "register",
        Drawer: "dashboard",
        EmployeeLoginScreen: "employee-login",
        EmployeeRegistrationScreen: "employee-register",
        QuestionnaireTopicsScreen: "topics",
        QuestionsScreen: "questionnaire",
      },
    },
  };

  return (
    <NavigationContainer linking={linking} fallback={<Text>Loading...</Text>}>
      <Stack.Navigator
        initialRouteName={companyId ? "Drawer" : "CompanyLoginScreen"}
        screenOptions={{
          headerStyle: {
            backgroundColor: GlobalStyles.btnColor.backgroundColor,
          },
          headerTintColor: "#fff",
          headerTitleStyle: {
            ...GlobalStyles.btntxtoffset,
            fontSize: isDesktopWeb
              ? GlobalStyles.fontSize.lg
              : GlobalStyles.fontSize.xl,
            fontFamily: GlobalStyles.GlobalFont.fontFamily,
            paddingVertical: 1,
          },
          headerTitleAlign: "center",
          headerShown: false,
          headerRight: () => (
            <Image
              source={require("./assets/ESG_logo.png")}
              style={{
                width: 40,
                height: 40,
                marginRight: 30,
                borderRadius: 20,
              }}
              resizeMode="contain"
            />
          ),
        }}
      >
        <Stack.Screen
          name="CompanyLoginScreen"
          component={CompanyLoginScreen}
        />
        <Stack.Screen
          name="CompanyRegistrationScreen"
          component={CompanyRegistrationScreen}
          options={{ title: "Company Registration" }}
        />
        <Stack.Screen
          name="Drawer"
          component={Drawer}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="EmployeeLoginScreen"
          component={EmployeeLoginScreen}
          options={{ title: "Employee Login" }}
        />
        <Stack.Screen
          name="EmployeeRegistrationScreen"
          component={EmployeeRegistrationScreen}
          options={{ title: "Employee Registration" }}
        />
        <Stack.Screen
          name="QuestionnaireTopicsScreen"
          component={QuestionnaireTopicsScreen}
          options={{ title: "Topics" }}
        />
        <Stack.Screen
          name="QuestionsScreen"
          component={QuestionsScreen}
          options={{ title: "Questionnaire" }}
        />
      </Stack.Navigator>
    </NavigationContainer>
  );
};

/* ------------------ ✅ ROOT APP COMPONENT ------------------ */
export default function App() {
  const [appIsReady, setAppIsReady] = useState(false);

  useEffect(() => {
    async function prepare() {
      try {
        await Font.loadAsync({
          LatoBlack: require("./assets/fonts/Lato-Black.ttf"),
          LatoRegular: require("./assets/fonts/Lato-Regular.ttf"),
        });
      } catch (e) {
        console.warn(e);
      } finally {
        setAppIsReady(true);
        await SplashScreen.hideAsync();
      }
    }
    prepare();
  }, []);

  const onLayoutRootView = useCallback(async () => {
    if (appIsReady) {
      await SplashScreen.hideAsync();
    }
  }, [appIsReady]);

  if (!appIsReady) return null;

  return (
    <View style={{ flex: 1 }} onLayout={onLayoutRootView}>
      <AuthProvider>
        <NetworkProvider>
          <MainNavigator />
        </NetworkProvider>
      </AuthProvider>
    </View>
  );
}
